import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { VendorService } from '../../services/vendor.service';
import { resolveDefaultBankAccountCode } from '../../utils/bank-account-default';
import { buildDashboardThemeVars } from '../../shared/dashboard-widget-theme';
import {
	VendorRemitPreviewModalComponent,
	VendorRemitPreviewModalData,
	VendorRemitPreviewTabRequest,
} from '../../shared/vendor-remit-preview-modal/vendor-remit-preview-modal.component';

/**
 * Streamlined "pay one vendor" form. Two modes, picked by whether a payoutRow
 * is passed in:
 *  - Brand mode (Brand Payouts Pending dashboard): pre-fills amount from the
 *    payout row and only rolls across invoices whose customers have paid.
 *  - Generic mode (Over Due Vendors, or any plain vendor): every open invoice
 *    is eligible; amount pre-fills from suggestedAmount (e.g. the past-due total).
 * Either way it auto-selects invoices oldest-first, applies on-account credits,
 * and posts payment + allocation in a single click.
 */
@Component({
	selector: 'app-vendor-brand-payout-entry',
	templateUrl: './vendor-brand-payout-entry.component.html',
	styleUrls: ['./vendor-brand-payout-entry.component.scss'],
})
export class VendorBrandPayoutEntryComponent implements OnInit {
	@Input() vendor: any;
	@Input() payoutRow: any = null;
	/** Generic mode: pre-fill the payment amount with this (e.g. vendor's past-due total). */
	@Input() suggestedAmount: number | null = null;
	@Output() posted = new EventEmitter<void>();
	@Output() cancelled = new EventEmitter<void>();

	form: UntypedFormGroup;
	today = new Date();

	bankaccounts: any[] = [];
	payment_methods: any[] = [];

	invoices: any[] = [];
	availableCredits: any[] = [];
	currencies: any[] = [];
	loadingInvoices = true;

	private suppressAmountSync = false;
	/** Guards the one-shot re-request of payment methods when globals weren't primed. */
	private requestedPaymentMethods = false;

	posting = false;
	previewingCheck = false;

	readonly Math = Math;
	color = 'blue';

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('bp', this.color);
	}

	constructor(
		private fb: UntypedFormBuilder,
		private paymentsService: PaymentsService,
		private globalSearchService: GlobalSearchService,
		private vendorService: VendorService,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		const netPayout = this.toNum(this.payoutRow?.net_payout_to_brand);
		const suggested = this.toNum(this.suggestedAmount);
		const brandName = this.payoutRow?.suppname || this.vendor?.suppname || this.vendor?.supplierid;
		const initialAmount = this.brandMode
			? (netPayout > 0 ? this.round2(netPayout) : 0)
			: (suggested > 0 ? this.round2(suggested) : 0);

		this.form = this.fb.group({
			payment_type: ['', Validators.required],
			bankaccount: ['', Validators.required],
			datepaid: [this.today, Validators.required],
			reference: [''],
			checkno: [''],
			description: [`${this.brandMode ? 'Brand Payout' : 'Vendor Payment'} – ${brandName}`, Validators.required],
			amount: [initialAmount, Validators.required],
		});

		this.form.get('amount').valueChanges
			.pipe(debounceTime(300), distinctUntilChanged())
			.subscribe(() => {
				if (this.suppressAmountSync) {
					return;
				}
				// Default roll is whole-invoice only — Partial is an explicit button click.
				this.autoBalance(false);
			});

		combineLatest([
			this.globalSearchService.bankaccounts,
			this.globalSearchService.company,
		])
			.pipe(filter(([accounts]) => Array.isArray(accounts) && accounts.length > 0))
			.subscribe(([accounts, company]) => {
				this.bankaccounts = accounts;
				this.form.get('bankaccount').setValue(
					resolveDefaultBankAccountCode(accounts, company?.defaultBankAccount),
				);
			});

		this.globalSearchService.payment_methods.subscribe((methods: any) => {
			this.payment_methods = methods || [];
			if (!methods?.length) {
				// Payment method is required to post, so an unprimed globals cache would
				// leave the form permanently invalid with no way for the operator to fix
				// it. Ask for the list once instead of rendering an empty chip row.
				if (!this.requestedPaymentMethods) {
					this.requestedPaymentMethods = true;
					this.globalSearchService.updatePaymentMethods();
				}
				return;
			}
			// Only set default if the user (or earlier emission) hasn't picked one yet
			const current = this.form.value.payment_type;
			if (current && methods.find((m: any) => m.paymentname === current)) { return; }
			this.form.get('payment_type').setValue(this.pickDefaultMethod(methods));
		});

		this.globalSearchService.currencies.subscribe((currencies: any) => {
			this.currencies = currencies || [];
		});

		this.loadInvoices();
	}

	private loadInvoices(): void {
		if (!this.vendor?.supplierid) {
			this.loadingInvoices = false;
			return;
		}
		this.paymentsService.getVendorAccountPayables(this.vendor.supplierid).subscribe((results: any) => {
			const invoices = results?.invoices || [];
			// The payables endpoint carries the brand (customer-paid) enrichment, but it
			// returns nothing when its SQL can't run on this tenant. The vendor
			// transactions endpoint has no such dependency, so fall back to it rather
			// than show "no outstanding invoices" for a vendor that plainly owes money.
			if (!invoices.length) {
				this.loadInvoicesFromTransactions();
				return;
			}
			this.invoices = invoices.map((inv: any) => ({
				...inv,
				selected: false,
				allocation: 0,
				creditAlloc: 0,
				customer_paid_amount: this.toNum(inv.customer_paid_amount),
				customer_invoice_gross: this.toNum(inv.customer_invoice_gross),
				brand_portion: this.toNum(inv.brand_portion),
				customer_paid: !!inv.customer_paid,
				brand_name: inv.suppname || this.vendor?.suppname || '',
				brand_id: inv.supplierno || this.vendor?.supplierid || '',
			}));
			const onAccountCredits = [...(results?.payments || []), ...(results?.credits || [])]
				.filter((c: any) => this.toNum(c.leftto) > 0)
				.map((c: any) => ({
					...c,
					leftto: this.round2(this.toNum(c.leftto)),
					selected: true,
				}));
			this.availableCredits = onAccountCredits.sort((a, b) => {
				const aDate = a.trandate ? new Date(a.trandate).getTime() : 0;
				const bDate = b.trandate ? new Date(b.trandate).getTime() : 0;
				if (aDate !== bDate) { return aDate - bDate; }
				return this.toNum(a.transno) - this.toNum(b.transno);
			});
			// Display order must MATCH the roll order so the payment visibly walks the
			// list top→bottom: eligible invoices first (customer-paid in brand mode;
			// everything in generic mode), then within each group oldest invoice
			// first — the same key (invoiceAge) the roll fills by.
			this.invoices.sort((a, b) => {
				const aReady = this.isEligible(a) ? 1 : 0;
				const bReady = this.isEligible(b) ? 1 : 0;
				if (aReady !== bReady) { return bReady - aReady; }
				return this.invoiceAge(a) - this.invoiceAge(b); // oldest invoice first
			});
			this.autoBalance(false);
			this.loadingInvoices = false;
		}, () => {
			this.loadingInvoices = false;
		});
	}

	/**
	 * Fallback source for the invoice list: the same open-transactions feed the
	 * vendor dashboards use. Type 20 rows with a balance become payable invoices;
	 * type 21 credits become on-account credit. No customer-paid enrichment exists
	 * here, so every invoice is eligible — which is already how generic mode rolls.
	 */
	private loadInvoicesFromTransactions(): void {
		const today = new Date();
		const fiveYearsAgo = new Date();
		fiveYearsAgo.setFullYear(today.getFullYear() - 5);

		this.vendorService.getVendorTransactions({
			supplierid: this.vendor.supplierid,
			opentrans: true,
			types: [20, 21],
			fromdate: fiveYearsAgo,
			todate: today,
			keywords: '',
		}).subscribe((res: any) => {
			const rows: any[] = Array.isArray(res?.transactions) ? res.transactions : [];

			this.invoices = rows
				.filter((t) => +t.type === 20 && this.toNum(t.balance) > 0.005)
				.map((t) => ({
					...t,
					leftto: this.round2(this.toNum(t.balance)),
					total: this.round2(this.toNum(t.totalamount)),
					reference: t.suppreference,
					selected: false,
					allocation: 0,
					creditAlloc: 0,
					customer_paid_amount: 0,
					customer_invoice_gross: 0,
					brand_portion: 0,
					customer_paid: false,
					brand_name: t.suppname || this.vendor?.suppname || '',
					brand_id: t.supplierno || this.vendor?.supplierid || '',
				}));

			this.availableCredits = rows
				.filter((t) => +t.type === 21 && this.toNum(t.balance) < -0.005)
				.map((t) => ({
					...t,
					leftto: this.round2(Math.abs(this.toNum(t.balance))),
					selected: true,
				}))
				.sort((a, b) => {
					const aDate = a.trandate ? new Date(a.trandate).getTime() : 0;
					const bDate = b.trandate ? new Date(b.trandate).getTime() : 0;
					if (aDate !== bDate) { return aDate - bDate; }
					return this.toNum(a.transno) - this.toNum(b.transno);
				});

			this.invoices.sort((a, b) => this.invoiceAge(a) - this.invoiceAge(b));
			this.autoBalance(false);
			this.loadingInvoices = false;
		}, () => {
			this.loadingInvoices = false;
		});
	}

	/** Check a row to include it (full balance), uncheck to drop it. A manual edit;
	 *  it stays until you click Auto/Partial or change the amount (which re-roll). */
	toggleInvoice(inv: any, checked: boolean): void {
		inv.selected = checked;
		inv.allocation = checked ? this.round2(this.toNum(inv.leftto)) : 0;
		if (!checked) { inv.creditAlloc = 0; }
		this.redistributeCredits();
	}

	/** Operator typed a $ amount on a row — a manual override of that line. */
	updateAllocation(inv: any, raw: any): void {
		const max = this.round2(this.toNum(inv.leftto));
		const val = Math.max(0, Math.min(max, this.round2(this.toNum(raw))));
		inv.allocation = val;
		inv.selected = val > 0.005 || this.toNum(inv.creditAlloc) > 0.005;
		if (!inv.selected) { inv.allocation = 0; inv.creditAlloc = 0; }
		this.redistributeCredits();
	}

	/**
	 * "All" — take every eligible invoice at its full balance. That ignores whatever
	 * was in the amount box, so the wire is re-set to the cash the picks actually
	 * need (credits already netted out by redistributeCredits).
	 */
	selectAll(): void {
		this.invoices.forEach((inv) => {
			if (this.toNum(inv.leftto) > 0 && this.isEligible(inv)) {
				inv.selected = true;
				inv.allocation = this.round2(this.toNum(inv.leftto));
			}
		});
		this.redistributeCredits();
		if (this.selectedTotal > 0.005) {
			this.snapWireToSelection();
		}
	}

	clearAll(): void {
		this.invoices.forEach((inv) => {
			inv.selected = false;
			inv.allocation = 0;
			inv.creditAlloc = 0;
		});
	}

	/**
	 * Rolls two independent pools across invoices oldest→newest: selected on-account
	 * credit goes on FIRST (it's already-paid cash, costs nothing new), then entered
	 * cash covers the remaining room. Clears any prior selection and re-fills from
	 * scratch — run by the Whole/Partial buttons, the amount field, and initial load.
	 * Manual row edits (check / typed Pay) are left untouched until one of those
	 * re-rolls; those paths call redistributeCredits() instead to keep credit landed.
	 */
	autoBalance(allowPartial = true): void {
		this.invoices.forEach((inv) => {
			inv.selected = false;
			inv.allocation = 0;
			inv.creditAlloc = 0;
		});

		// Two independent pools rolled oldest→newest: selected on-account credits
		// (already-paid cash carried forward — costs nothing new) go on FIRST, then
		// new cash covers whatever room is left. Credit is NOT capped by the cash
		// entered — it lands on invoices on its own, so a leftover credit gets used
		// next round even with little or no new payment.
		let credit = this.creditPool;
		let cash = this.paymentAmount;

		const candidates = this.invoices
			.filter((inv) => this.isEligible(inv) && this.toNum(inv.leftto) > 0)
			.sort((a, b) => this.invoiceAge(a) - this.invoiceAge(b));

		for (const cand of candidates) {
			const room = this.round2(this.toNum(cand.leftto));
			if (room <= 0.005) { continue; }

			const c = this.round2(Math.min(Math.max(0, credit), room));
			const roomAfterCredit = this.round2(room - c);
			let pay = this.round2(Math.min(Math.max(0, cash), roomAfterCredit));

			// Whole mode: don't cash-partial an invoice that no credit touched — let
			// it roll forward instead. (allowPartial lets the last invoice split.)
			if (!allowPartial && c <= 0.005 && pay > 0.005 && pay + 0.005 < roomAfterCredit) {
				pay = 0;
			}

			if (c > 0.005 || pay > 0.005) {
				cand.selected = true;
				cand.creditAlloc = c;
				cand.allocation = pay;
				credit = this.round2(credit - c);
				cash = this.round2(cash - pay);
			}

			if (credit <= 0.005 && cash <= 0.005) { break; }
		}
	}

	/**
	 * Recompute credit distribution across the CURRENTLY selected invoices
	 * (oldest→newest) from the selected-credit pool, and cap each invoice's cash so
	 * credit + cash never exceeds its balance. Called after any manual toggle/edit so
	 * on-account credit always lands (and is freely removed by deselecting a credit).
	 */
	private redistributeCredits(): void {
		let budget = this.creditPool;
		this.invoices.forEach((i) => { i.creditAlloc = 0; });
		const selected = this.invoices
			.filter((i) => i.selected)
			.sort((a, b) => this.invoiceAge(a) - this.invoiceAge(b));
		for (const inv of selected) {
			const room = this.round2(this.toNum(inv.leftto));
			const c = this.round2(Math.min(Math.max(0, budget), room));
			inv.creditAlloc = c;
			budget = this.round2(budget - c);
			const maxCash = this.round2(room - c);
			if (this.toNum(inv.allocation) > maxCash) { inv.allocation = maxCash; }
		}
	}

	/**
	 * Running pool left after this SELECTED invoice is applied — lets the operator
	 * watch the payment walk down the list. Starts from the post-recovery remainder
	 * and subtracts each selected invoice in display (oldest→newest) order.
	 */
	rollingLeftAfter(inv: any): number {
		if (!inv?.selected) { return 0; }
		let remaining = this.paymentAmount;
		for (const row of this.invoices) {
			if (!row.selected) { continue; }
			remaining = this.round2(remaining - this.toNum(row.allocation));
			if (+row.id === +inv.id) { break; }
		}
		return Math.max(0, remaining);
	}

	/**
	 * Operator-triggered: collapse the payment amount to what the current
	 * distribution actually totals — invoices plus recovery consumed. Never runs
	 * automatically — only when the user clicks "Snap to fit".
	 */
	snapWireToSelection(): void {
		const target = this.round2(this.selectedTotal);
		const current = this.round2(this.toNum(this.form?.value?.amount));
		if (Math.abs(target - current) < 0.005) { return; }
		this.suppressAmountSync = true;
		try {
			this.form.get('amount').setValue(target, { emitEvent: false });
		} finally {
			this.suppressAmountSync = false;
		}
	}

	/** Brand mode = launched from the Brand Payouts dashboard with a payout row. */
	get brandMode(): boolean {
		return !!this.payoutRow;
	}

	/** Which invoices the roll may land on: customer-paid only in brand mode; all in generic. */
	isEligible(inv: any): boolean {
		return this.brandMode ? !!inv?.customer_paid : true;
	}

	/** Sum of every open invoice balance — the generic-mode headline number. */
	get totalOpen(): number {
		return this.round2(this.invoices.reduce((sum, i) => sum + this.toNum(i.leftto), 0));
	}

	/** Generic-mode summary metric: the passed-in past-due total, else total open AP. */
	get genericDueDisplay(): number {
		const s = this.toNum(this.suggestedAmount);
		return s > 0 ? this.round2(s) : this.totalOpen;
	}

	private invoiceAge(inv: any): number {
		const raw = inv?.trandate;
		if (!raw) { return Number.POSITIVE_INFINITY; }
		const t = new Date(raw).getTime();
		return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
	}

	/** Total on-account credit available from the selected credit rows. */
	get creditPool(): number {
		return this.round2(this.selectedCredits.reduce(
			(sum, c) => sum + this.toNum(c.leftto),
			0,
		));
	}

	/** On-account credit actually landed on the selected invoices this run. */
	get creditAppliedTotal(): number {
		return this.round2(this.invoices.reduce(
			(sum, i) => sum + (i.selected ? this.toNum(i.creditAlloc) : 0),
			0,
		));
	}

	/** Credit applied that traces back to one credit row (FIFO), for its display. */
	creditAppliedForCredit(credit: any): number {
		let budget = this.creditAppliedTotal;
		for (const c of this.selectedCredits) {
			if (budget <= 0.005) { break; }
			const take = this.round2(Math.min(this.toNum(c.leftto), budget));
			if (+c.id === +credit.id) { return take; }
			budget = this.round2(budget - take);
		}
		return 0;
	}

	get rollsForward(): number {
		if (this.dashboardNetPayout <= 0) {
			return 0;
		}
		return Math.max(0, this.round2(this.dashboardNetPayout - this.paymentAmount));
	}

	get overpaysBrand(): number {
		if (this.dashboardNetPayout <= 0) {
			return 0;
		}
		return Math.max(0, this.round2(this.paymentAmount - this.dashboardNetPayout));
	}

	get selectedCredits(): any[] {
		return this.availableCredits.filter((c) => c.selected && this.toNum(c.leftto) > 0);
	}

	get hasCredits(): boolean {
		return this.availableCredits.length > 0;
	}

	toggleCredit(credit: any, checked: boolean): void {
		credit.selected = checked;
		// Re-apply credit across the already-selected invoices without disturbing the
		// operator's invoice picks/cash edits — so ticking a credit off simply removes it.
		this.redistributeCredits();
	}

	get dashboardNetPayout(): number {
		return this.round2(this.toNum(this.payoutRow?.net_payout_to_brand));
	}

	get dashboardLinkReady(): number {
		return this.round2(this.toNum(this.payoutRow?.link_ready));
	}

	get dashboardMetrcWithhold(): number {
		return this.round2(this.toNum(this.payoutRow?.metrc_withhold));
	}

	/** Excise + fees fronted on shipped-but-uncollected orders. The only thing
	 *  deducted from Available to reach the payable figure. */
	get dashboardPayoutHold(): number {
		return this.round2(this.toNum(this.payoutRow?.fronted_uncollected));
	}

	get selectedInvoices(): any[] {
		return this.invoices.filter((i) => i.selected
			&& (this.toNum(i.allocation) > 0.005 || this.toNum(i.creditAlloc) > 0.005));
	}

	/** New cash settled on invoices = the "To invoices" footer line. */
	get selectedTotal(): number {
		return this.round2(this.selectedInvoices.reduce((sum, i) => sum + this.toNum(i.allocation), 0));
	}

	get paymentAmount(): number {
		return this.round2(this.toNum(this.form?.value?.amount));
	}

	/** New cash that has a home on invoices (credit isn't cash, so not counted here). */
	get cashAllocationTotal(): number {
		return this.selectedTotal;
	}

	/** New cash entered that hasn't landed on an invoice (rolls forward as credit). */
	get unallocated(): number {
		return this.round2(this.paymentAmount - this.selectedTotal);
	}

	/** Total settled on an invoice = credit applied + new cash. */
	invoiceSettled(inv: any): number {
		return this.round2(this.toNum(inv?.creditAlloc) + this.toNum(inv?.allocation));
	}

	get hasOverAllocation(): boolean {
		return this.selectedInvoices.some((inv) => {
			const left = this.round2(this.toNum(inv.leftto));
			return this.invoiceSettled(inv) > left + 0.005;
		});
	}

	isPartial(inv: any): boolean {
		const left = this.round2(this.toNum(inv?.leftto));
		const settled = this.invoiceSettled(inv);
		return inv?.selected && settled > 0.005 && settled + 0.005 < left;
	}

	// Portion of the brand due that has ALREADY been paid on prior runs.
	// Brand due (customer_paid_amount) is the gross owed; leftto is what's still open.
	// The gap is what we've already disbursed — surfaced so a partially-paid invoice
	// doesn't look like a full re-payout is owed.
	paidAlready(inv: any): number {
		const due = this.toNum(inv?.customer_paid_amount);
		const left = this.toNum(inv?.leftto);
		return this.round2(Math.max(0, due - left));
	}

	get partialInvoices(): any[] {
		return this.invoices.filter((i) => this.isPartial(i));
	}

	get hasPartials(): boolean {
		return this.partialInvoices.length > 0;
	}

	get canPost(): boolean {
		return !this.posting
			&& this.form?.valid
			// Something must land: new cash on invoices, or on-account credit applied.
			// A credit-only round (no new cash) is allowed so leftover credit gets used.
			&& (this.selectedTotal > 0 || this.creditAppliedTotal > 0)
			&& !this.hasOverAllocation
			// Leftover cash (unallocated > 0) is allowed — it posts as on-account credit
			// on the brand. Only block when cash allocations exceed the payment entered.
			&& this.unallocated > -0.01
			&& !!this.vendor?.supplierid;
	}

	post(): void {
		if (!this.canPost) {
			return;
		}
		this.posting = true;

		const payment = {
			...this.form.value,
			amount: this.paymentAmount,
			discount: 0,
			currcode: this.vendor?.currcode || '',
			exrate: this.rateForCurrcode(this.vendor?.currcode || ''),
		};

		// New cash lands on the selected invoices (payment→invoice). Only the cash
		// portion goes here; the credit portion of each invoice is posted separately
		// as credit_allocations below. Leftover cash rolls forward as on-account credit.
		const allocations_to = this.selectedInvoices
			.map((inv) => ({
				...inv,
				allocation: this.round2(this.toNum(inv.allocation)),
			}))
			.filter((inv) => inv.allocation > 0.005);

		const payload: any = {
			payments: [payment],
			vendor: this.vendor,
			to: allocations_to,
		};

		// On-account credit lands on the selected invoices (credit→invoice), settling
		// invoice balance without any new cash. Independent of the amount entered.
		const creditAllocations = this.buildRecoveryAllocations();
		if (creditAllocations.length) {
			payload.credit_allocations = creditAllocations;
		}

		this.paymentsService.addVendorPayment(payload).subscribe(
			(res: any) => {
				this.globalSearchService.showNotification('Payment posted', 'success', 'bottom', 'right');
				this.posting = false;
				// A check payment isn't finished until the clerk has the check itself.
				if (this.isCheckPayment(payment.payment_type)) {
					this.downloadCheckPdf(res?.payment_id);
				}
				this.openRemitPreview(payment, this.selectedInvoices);
			},
			() => {
				this.globalSearchService.showNotification('Failed to post payment', 'danger', 'bottom', 'right');
				this.posting = false;
			},
		);
	}

	/** Check-style methods that should produce a printable check on post. */
	isCheckPayment(method: any): boolean {
		const m = String(method || '').toLowerCase().trim();
		return m === 'check' || m === 'cheque' || m.startsWith('check ');
	}

	/** True when the currently selected method will produce a check. */
	get payingByCheck(): boolean {
		return this.isCheckPayment(this.form?.value?.payment_type);
	}

	/**
	 * Render the check for what's on screen WITHOUT posting, so the clerk can eyeball
	 * alignment and the stub before committing. Server-side this touches no tables.
	 */
	previewCheck(): void {
		if (!this.vendor?.supplierid || this.previewingCheck) {
			return;
		}
		this.previewingCheck = true;
		const payload = {
			preview: true,
			supplierid: this.vendor.supplierid,
			checkno: this.form.value.checkno || '',
			trandate: this.formatPostDate(this.form.value.datepaid),
			amount: this.paymentAmount,
			lines: this.selectedInvoices.map((inv) => ({
				inv: inv.suppreference || inv.reference || inv.transno,
				date: inv.trandate,
				bal: this.round2(this.toNum(inv.leftto)),
				pay: this.invoiceSettled(inv),
				disc: 0,
			})),
		};
		this.paymentsService.getSingleCheckPdf(payload).subscribe(
			(res: any) => {
				this.previewingCheck = false;
				if (res?.success && res?.base64content) {
					this.globalSearchService.downloadPdf(
						res.base64content,
						res.filename || 'Check-Preview.pdf',
					);
				} else {
					this.globalSearchService.showNotification(
						res?.error || 'Could not build the check preview.', 'warning', 'bottom', 'right');
				}
			},
			() => {
				this.previewingCheck = false;
				this.globalSearchService.showNotification(
					'Could not build the check preview.', 'warning', 'bottom', 'right');
			},
		);
	}

	/**
	 * Pull the check PDF for the payment we just posted and hand it to the browser.
	 * Rendering is a read-only server call — it prints what was posted, it does not
	 * post or allocate anything, so a failure here never costs the payment.
	 */
	private downloadCheckPdf(paymentId: any): void {
		const id = +paymentId;
		if (!Number.isFinite(id) || id <= 0) {
			this.globalSearchService.showNotification(
				'Payment posted, but the check could not be identified to print.', 'warning', 'bottom', 'right');
			return;
		}
		this.paymentsService.getSingleCheckPdf({ payment_id: id }).subscribe(
			(res: any) => {
				if (res?.success && res?.base64content) {
					this.globalSearchService.downloadPdf(
						res.base64content,
						res.filename || `Check-${res.checkNum || id}.pdf`,
					);
				} else {
					this.globalSearchService.showNotification(
						res?.error || 'Payment posted, but the check PDF could not be generated.',
						'warning', 'bottom', 'right');
				}
			},
			() => {
				this.globalSearchService.showNotification(
					'Payment posted, but the check PDF could not be generated.', 'warning', 'bottom', 'right');
			},
		);
	}

	/**
	 * Open the remit-email preview modal showing the email that was sent for the just-posted payment.
	 * Emits `posted` after the modal closes so the parent (dashboard modal) can refresh.
	 */
	private openRemitPreview(payment: any, allocations_to: any[]): void {
		// Lines show the GROSS amount that settled each document (cash + credit),
		// so the remit reconciles: documents total − credit applied = cash paid.
		const remitAllocations = allocations_to
			.map((inv) => ({
				trans_id: +inv.id,
				amount: this.round2(this.toNum(inv.allocation)),
			}))
			.filter((a) => Number.isFinite(a.trans_id) && a.amount > 0);

		if (!remitAllocations.length) {
			this.posted.emit();
			return;
		}

		const currency = this.vendor?.currcode || 'USD';
		const tab: VendorRemitPreviewTabRequest = {
			label: `${payment.payment_type || 'Payment'}`,
			supplier_id: this.vendor.supplierid,
			context: {
				payment_total: this.toNum(payment.amount),
				payment_discount: 0,
				payment_reference: payment.reference || '',
				checkno: payment.checkno || '',
				payment_type: payment.payment_type || '',
				post_date: this.formatPostDate(payment.datepaid),
				bank_account: payment.bankaccount || '',
				currency,
				credit_applied: this.creditAppliedTotal,
				credit_lines: this.buildRemitCreditLines(),
			},
			allocations: remitAllocations,
		};

		const ref = this.dialog.open(VendorRemitPreviewModalComponent, {
			width: '760px',
			maxWidth: '98vw',
			// Stack above the parent NgbModal (which sits at z-index 1055).
			panelClass: 'vendor-remit-over-modal',
			backdropClass: 'vendor-remit-over-modal-backdrop',
			data: {
				title: 'Remittance Advice',
				tabs: [tab],
			} as VendorRemitPreviewModalData,
		});

		ref.afterClosed().subscribe(() => this.posted.emit());
	}

	/**
	 * Recovery→invoice allocations for the post. Each consumed recovery is paid
	 * down against the oldest selected invoice that still has room beyond the cash
	 * landing on it, FIFO. Keeps the owed-back balance settled without new cash.
	 */
	private buildRecoveryAllocations(): Array<{ from_id: number; to_id: number; amt: number }> {
		const out: Array<{ from_id: number; to_id: number; amt: number }> = [];
		// Each invoice's credit demand (what redistributeCredits parked on it), drawn
		// FIFO from the selected credit rows so every from→to pair is explicit.
		const credits = this.selectedCredits.map((c) => ({
			from_id: +c.id,
			left: this.round2(this.toNum(c.leftto)),
		}));
		let ci = 0;
		for (const inv of this.selectedInvoices) {
			let demand = this.round2(this.toNum(inv.creditAlloc));
			while (demand > 0.005 && ci < credits.length) {
				const slot = credits[ci];
				if (slot.left <= 0.005) { ci++; continue; }
				const take = this.round2(Math.min(slot.left, demand));
				out.push({ from_id: slot.from_id, to_id: +inv.id, amt: take });
				slot.left = this.round2(slot.left - take);
				demand = this.round2(demand - take);
				if (slot.left <= 0.005) { ci++; }
			}
		}
		return out;
	}

	/**
	 * One reconciliation row per recovery actually consumed, for the remit advice.
	 */
	private buildRemitCreditLines(): Array<{ reference: string; trandate: string; amount: number }> {
		return this.selectedCredits
			.map((credit) => {
				const amount = this.creditAppliedForCredit(credit);
				if (amount <= 0.005) { return null; }
				const reference = credit.suppreference || credit.reference
					|| (credit.transno ? `#${credit.transno}` : '');
				return {
					reference: String(reference || ''),
					trandate: String(credit.trandate || ''),
					amount,
				};
			})
			.filter((l): l is { reference: string; trandate: string; amount: number } => !!l);
	}

	formatPostDate(d: any): string {
		if (d instanceof Date) {
			return d.toISOString().slice(0, 10);
		}
		if (typeof d === 'string' && d.length >= 10) {
			return d.slice(0, 10);
		}
		return new Date().toISOString().slice(0, 10);
	}

	cancel(): void {
		this.cancelled.emit();
	}

	/**
	 * Public "Whole" button — roll oldest→newest, whole invoices only, then collapse
	 * the payment amount to what actually landed. Whole-invoice rolls almost never
	 * consume the entered amount exactly, and leaving the difference in the box turns
	 * it into an on-account credit nobody asked for; the operator wants to wire what
	 * the invoices come to. The shortfall vs the dashboard shows as "Remaining paid".
	 * Skips the collapse when nothing fit, so the amount isn't zeroed out (which would
	 * disable the button and strand the form).
	 */
	fillSelectedToAmount(): void {
		this.autoBalance(false);
		if (this.selectedTotal > 0.005) {
			this.snapWireToSelection();
		}
	}

	/** Public "Partial" button — same roll, but partial-pay the last invoice so the
	 *  full payment lands on invoices (nothing rolls forward as credit). */
	fillWithPartials(): void {
		this.autoBalance();
	}

	private rateForCurrcode(code: string): number {
		const cur = (this.currencies || []).find((c: any) => c.currabrev === code);
		if (cur) {
			const r = parseFloat(cur.rate);
			if (!isNaN(r) && r > 0) { return r; }
		}
		const vendorRate = parseFloat(this.vendor?.exrate);
		return (!isNaN(vendorRate) && vendorRate > 0) ? vendorRate : 1;
	}

	/**
	 * Prefer Moov for vendor payouts (electronic, no check to mail).
	 * Falls back to ACH/EFT/Wire/Bank-style names, then Check, then the first method.
	 */
	private pickDefaultMethod(methods: any[]): string {
		const names = methods.map((m: any) => ({ name: m.paymentname, lower: String(m.paymentname || '').toLowerCase() }));
		const findBy = (pred: (n: { name: string; lower: string }) => boolean) => names.find(pred)?.name;
		return (
			findBy((n) => n.lower.includes('moov')) ||
			findBy((n) => n.lower === 'ach' || n.lower.startsWith('ach ')) ||
			findBy((n) => n.lower.includes('eft') || n.lower.includes('electronic')) ||
			findBy((n) => n.lower.includes('wire') || n.lower.includes('bank transfer')) ||
			findBy((n) => n.lower === 'check' || n.lower === 'cheque') ||
			methods[0].paymentname
		);
	}

	toNum(v: any): number {
		if (v == null || v === '') {
			return 0;
		}
		if (typeof v === 'number') {
			return v;
		}
		const cleaned = String(v).replace(/[^\d.-]/g, '');
		const n = parseFloat(cleaned);
		return isNaN(n) ? 0 : n;
	}

	private round2(n: number): number {
		return Math.round(n * 100) / 100;
	}
}
